import React, { Children, ComponentProps, PropsWithChildren } from 'react';
import { StyleSheet, useWindowDimensions, ViewProps } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { HStack, useResponsive } from '@garnish/core';
import { HorizontalScrollRail } from '@garnish/layout';

import { useHomeContainerSpacing } from '../../hooks';
import { HOME_PRODUCT_CARD_SIZES_REGULAR, HOME_PRODUCT_CARD_SIZES_LARGE } from '../HomeProductCard';

import type { HomeProductCardSize } from '../HomeProductCard/HomeProductCard.types';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeGenericCategory = (props: HomeGenericCategoryProps) => {
  const { size = 'regular', children } = props;

  const { currentBreakpoint } = useResponsive();
  const containerStyle = useHomeContainerSpacing();

  if (currentBreakpoint.isXS) {
    const columnGap = size === 'regular' ? styles.columnGapRegular : styles.columnGapLarge;

    return (
      <HorizontalScrollRail
        outerOffset={0}
        showNavControls={false}
        gap={theme.spacing['2']}
        contentContainerStyle={[containerStyle, columnGap]}
      >
        {children}
      </HorizontalScrollRail>
    );
  }

  return <DynamicHStack size={size}>{children}</DynamicHStack>;
};

export const HomeFilterableCategory = (props: PropsWithChildren) => {
  const { children } = props;

  const { match } = useResponsive();
  const filterableStyle = match([styles.filterableCategoryXS, styles.filterableCategorySM]);

  return <DynamicHStack style={filterableStyle}>{children}</DynamicHStack>;
};

const DynamicHStack = (props: DynamicHStackProps) => {
  const { size = 'regular', children, style } = props;

  const { match } = useResponsive();
  const { width: windowWidth } = useWindowDimensions();

  const containerStyle = useHomeContainerSpacing();
  const cardSizes = size === 'regular' ? HOME_PRODUCT_CARD_SIZES_REGULAR : HOME_PRODUCT_CARD_SIZES_LARGE; // prettier-ignore

  const childrenCount = Children.count(children);
  const shouldHaveOneItemPerRow = childrenCount === 1 || windowWidth < 350;
  const itemsPerRow = shouldHaveOneItemPerRow ? 1 : match([2, 2, 3]);
  const productCardSize = match(cardSizes);
  const containerSize = itemsPerRow * (productCardSize + GRID_GAP) + GRID_PADDING * 2;
  const containerSizeStyle = useStyle(() => ({ width: containerSize }), [containerSize]);

  const categoryStyle = match([containerStyle, styles.categoriesSM]);
  const containerStyles = [categoryStyle, match([null, containerSizeStyle]), style];

  return (
    <HStack
      style={containerStyles}
      itemsPerRow={shouldHaveOneItemPerRow ? 1 : match([2, 2, 3])}
      gap={match([theme.spacing['2'], theme.spacing['6']])}
      itemStyle={styles.itemStyle}
    >
      {children}
    </HStack>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const GRID_GAP = theme.spacing['6'];
const GRID_PADDING = theme.spacing['10'];

const styles = StyleSheet.create({
  flexView: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing['2'],
  },
  categoriesSM: {
    width: '100%',
    alignSelf: 'center',
    marginLeft: 0,
    marginRight: 0,
    paddingHorizontal: GRID_PADDING,
    rowGap: theme.spacing['8'],
  },
  filterableCategoryXS: {
    paddingVertical: theme.spacing['6'],
  },
  filterableCategorySM: {
    paddingVertical: theme.spacing['6'],
  },
  columnGapRegular: {
    columnGap: theme.spacing['2'],
  },
  columnGapLarge: {
    columnGap: theme.spacing['8'],
  },
  itemStyle: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeGenericCategoryProps = {
  size?: HomeProductCardSize;
  children: ComponentProps<typeof HorizontalScrollRail>['children'];
};

type DynamicHStackProps = {
  size?: HomeProductCardSize;
  children: PropsWithChildren['children'];
  style?: ViewProps['style'];
};
